import { Palette, PaletteColor, SxProps, Theme } from "@mui/material";
interface ItemOwnerState {
  active?: boolean | string;
  color: keyof Palette;
  nested?: boolean;
  disabled?: boolean;
  transparentSidenav?: boolean;
  whiteSidenav?: boolean;
  darkMode?: boolean;
}

interface ItemContentOwnerState {
  active?: boolean | string;
  miniSidenav?: boolean;
  name: string;
  open?: boolean;
  nested?: boolean;
  hasIcon?: boolean;
  transparentSidenav?: boolean;
  whiteSidenav?: boolean;
  darkMode?: boolean;
}

interface ItemArrowOwnerState {
  noCollapse?: boolean;
  transparentSidenav?: boolean;
  whiteSidenav?: boolean;
  miniSidenav?: boolean;
  open: boolean;
  active?: boolean;
  darkMode?: boolean;
}

function item(theme: Theme, ownerState: ItemOwnerState) {
  const { palette, borders, functions, transitions } = theme;
  const { active, color, nested, disabled, transparentSidenav, whiteSidenav, darkMode } = ownerState;

  const { transparent, white, grey } = palette;
  const { borderRadius } = borders;
  const { rgba } = functions;
  const isLightSidenav = (transparentSidenav && !darkMode) || whiteSidenav;

  return {
    pl: nested ? 1.5 : 3,
    ml: nested ? 2 : 0,
    mt: 0.375,
    mb: 0.3,
    width: nested ? "calc(100% - 16px)" : "100%",
    borderRadius: borderRadius.md,
    cursor: disabled ? "default" : "pointer",
    pointerEvents: disabled ? "none" : "auto",
    backgroundColor: () => {
      let backgroundValue = transparent.main;

      if (disabled) {
        backgroundValue = transparent.main;
      } else if (
        (active === "isParent" && !transparentSidenav && !whiteSidenav) ||
        (active === "isParent" && transparentSidenav && darkMode)
      ) {
        backgroundValue = rgba(white.main, 0.2);
      } else if (active === "isParent" && transparentSidenav) {
        backgroundValue = grey![300];
      } else if (active === "isParent" && whiteSidenav) {
        backgroundValue = grey![200];
      } else if (active) {
        backgroundValue = (palette[color] as PaletteColor).main;
      }

      return backgroundValue;
    },
    transition: transitions.create("background-color", {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.short,
    }),

    "&:hover, &:focus": {
      backgroundColor:
        !active &&
        !disabled &&
        rgba(isLightSidenav ? grey![400] : white.main, 0.2),
    },
  };
}

function itemContent(theme: Theme, ownerState: ItemContentOwnerState): SxProps<Theme> | undefined {
  const { palette, typography, transitions, functions } = theme;
  const { miniSidenav, name, active, nested, hasIcon, transparentSidenav, whiteSidenav, darkMode } =
    ownerState;

  const { white, dark } = palette;
  const { size, fontWeightRegular, fontWeightLight } = typography;
  const { pxToRem } = functions;
  const isLightSidenav = (transparentSidenav && !darkMode) || whiteSidenav;
  const markerColor = isLightSidenav && (active === "isParent" || !active) ? dark.main : white.main;

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: nested ? `${pxToRem(8)} ${pxToRem(12)}` : `${pxToRem(12)} ${pxToRem(16)}`,
    marginLeft: nested ? pxToRem(12) : pxToRem(18),
    userSelect: "none",
    position: "relative",
    color: markerColor,

    "& span": {
      fontFamily: typography.fontFamily,
      color: markerColor,
      fontWeight: active ? fontWeightRegular : fontWeightLight,
      fontSize: size!.sm,
      lineHeight: 1.625,
      opacity: miniSidenav ? 0 : 1,
      transition: transitions.create(["opacity", "color"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },

    "&::before": hasIcon
      ? {
          content: '""',
          display: "none",
        }
      : nested
        ? {
            content: '""',
            width: pxToRem(6),
            height: pxToRem(6),
            backgroundColor: markerColor,
            display: "block",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: pxToRem(-12),
            borderRadius: "50%",
          }
        : {
            content: `"${name?.[0] ?? ""}"`,
            fontFamily: typography.fontFamily,
            color: markerColor,
            fontWeight: fontWeightRegular,
            display: "flex",
            alignItems: "center",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            lineHeight: 1.625,
            left: pxToRem(-15),
            opacity: 1,
            borderRadius: "50%",
            fontSize: size!.sm,
          },
  };
}

function itemArrow(theme: Theme, ownerState: ItemArrowOwnerState) {
  const { palette, typography, transitions, breakpoints, functions } = theme;
  const { noCollapse, transparentSidenav, whiteSidenav, miniSidenav, open, active, darkMode } =
    ownerState;

  const { white, dark } = palette;
  const { size } = typography;
  const { pxToRem, rgba } = functions;

  return {
    fontSize: `${size!.lg} !important`,
    fontWeight: 700,
    marginBottom: pxToRem(-1),
    transform: open ? "rotate(0)" : "rotate(-180deg)",
    color: () => {
      let colorValue;

      if (transparentSidenav && darkMode) {
        colorValue = open || active ? white.main : rgba(white.main, 0.25);
      } else if (transparentSidenav || whiteSidenav) {
        colorValue = open || active ? dark.main : rgba(dark.main, 0.25);
      } else {
        colorValue = open || active ? white.main : rgba(white.main, 0.5);
      }

      return colorValue;
    },
    transition: transitions.create(["color", "transform", "opacity"], {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.shorter,
    }),

    [breakpoints.up("xl")]: {
      display:
        noCollapse || (transparentSidenav && miniSidenav) || miniSidenav
          ? "none !important"
          : "block !important",
    },
  };
}

export { item, itemContent, itemArrow };
